import React, { useState, FormEvent } from 'react';

import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faArrowsLeftRightToLine,
  faSquarePlus,
  faSnowflake,
  faCircleExclamation,
  faUpRightAndDownLeftFromCenter,
  faCalculator,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { useToastNotification } from '@/hooks';
import InfoButton from '@/components/InfoAlertActions/InfoAlertActions';
import { RadioButton } from 'primereact/radiobutton';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputNumberField } from '@/components';
import { Button } from 'primereact/button';
import classNames from 'classnames';
import { generateImport } from './generateImport';
import styles from './CostSimulator.module.scss';

export interface CostSimulatorImportFormState {
  dias: number;
  entregaDirecta: boolean;
  esIMDG: boolean;
  esOOG: boolean;
  esReeferOperativo: boolean;
  tamano: number;
}

interface CostSimulatorFormErrors {
  dias: string;
  entregaDirecta: string;
  esIMDG: string;
  esOOG: string;
  esReeferOperativo: string;
  tamano: string;
}

interface ICostSimulatorImportProps {
  setIsSliderOpen: (value: boolean) => void;
  setImportData: (
    result: null,
    formData: CostSimulatorImportFormState,
    total: number
  ) => void;
}

export const CostSimulatorImport: React.FC<ICostSimulatorImportProps> = ({
  setIsSliderOpen,
  setImportData,
}) => {
  const { t } = useTranslation('costSimulatorForm');
  const { showToast } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<CostSimulatorImportFormState>({
    dias: 0,
    entregaDirecta: false,
    esIMDG: false,
    esOOG: false,
    esReeferOperativo: false,
    tamano: 20,
  });

  const [formErrors, setFormErrors] = useState<CostSimulatorFormErrors>({
    dias: '',
    entregaDirecta: '',
    esIMDG: '',
    esOOG: '',
    esReeferOperativo: '',
    tamano: '',
  });

  const validateForm = () => {
    let errors: CostSimulatorFormErrors = { ...formErrors };

    if (formData.dias === 0 && !formData.entregaDirecta) {
      errors = { ...errors, entregaDirecta: 'error' };
    }
    if (formData.dias !== 0) {
      errors = { ...errors, entregaDirecta: '' };
    }
    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: keyof CostSimulatorImportFormState
  ) => {
    const { value, checked, type } = e.target;
    const updatedValue = type === 'checkbox' ? checked : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: updatedValue,
      ...(name === 'entregaDirecta' && checked ? { dias: 0 } : {}),
      ...(name === 'entregaDirecta' && checked ? { esIMDG: false } : {}),
      ...(name === 'entregaDirecta' && checked
        ? { esReeferOperativo: false }
        : {}),
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };
  const handleCheckboxChange = (
    name: keyof Omit<CostSimulatorImportFormState, 'dias' | 'tamano'>
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const generateImportMutation = useMutation({
    mutationFn: generateImport,
    onMutate: () => {
      setIsLoading(true);
      setIsSliderOpen(false);
    },
    onSuccess: (response) => {
      setImportData(
        response?.data?.value?.detalle,
        formData,
        response?.data?.value?.total
      );
      setIsSliderOpen(true);
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);

      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    validateForm();
    if (validateForm()) {
      generateImportMutation.mutate(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.cfeForm}>
      <div className={styles.formSection}>
        <label htmlFor="daysInTerminal" className={styles.formSectionTitle}>
          <FontAwesomeIcon icon={faCalendarAlt} className={styles.icon} />
          {t('daysInTerminal')}:
        </label>
        <div className={styles.daysInTerminalContainer}>
          <div className={styles.maxWidthInput}>
            <InputNumberField
              value={formData.dias}
              onChange={(e) => handleInputChange(e, 'dias')}
              id="daysInTerminal"
              label=""
              name="daysInTerminal"
              disabled={formData.entregaDirecta}
              allowOnlyWholeNumbers
              minNumber={0}
            />
          </div>
          <div className={styles.checkboxInputField}>
            <Checkbox
              inputId="directDelivery"
              checked={formData.entregaDirecta}
              onChange={(e) => handleInputChange(e, 'entregaDirecta')}
            />
            <label htmlFor="directDelivery">{t('directDelivery')}</label>
          </div>
        </div>
        {formErrors.entregaDirecta === 'error' && (
          <InfoButton type="warning" infoText={t('sameDayDirectDelivery')} />
        )}
      </div>
      <div className={styles.formSection}>
        <label className={styles.formSectionTitle}>
          <FontAwesomeIcon
            icon={faArrowsLeftRightToLine}
            className={styles.icon}
          />
          {t('sizeFeet')}:
        </label>
        <div className={styles.radioButtonsContainer}>
          <div className={styles.checkboxInputField}>
            <RadioButton
              inputId="size20"
              name="size"
              value={20}
              onChange={(e) => handleInputChange(e, 'tamano')}
              checked={formData.tamano === 20}
            />
            <label htmlFor="size20">{t('20Feet')}</label>
          </div>
          <div className={styles.checkboxInputField}>
            <RadioButton
              inputId="size40"
              name="size"
              value={40}
              onChange={(e) => handleInputChange(e, 'tamano')}
              checked={formData.tamano === 40}
            />
            <label htmlFor="size40">{t('40Feet')}</label>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles.formSection,
          styles.additionalCharacteristics
        )}
      >
        <label className={styles.formSectionTitle}>
          <FontAwesomeIcon icon={faSquarePlus} className={styles.icon} />
          {t('additionalFeatures')}
        </label>
        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esIMDG"
            checked={formData.esIMDG}
            onChange={() => handleCheckboxChange('esIMDG')}
            disabled={formData.entregaDirecta}
          />
          <label htmlFor="esIMDG">
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className={styles.icon}
            />
            {t('imdg')}
          </label>
        </div>
        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esOOG"
            checked={formData.esOOG}
            onChange={() => handleCheckboxChange('esOOG')}
          />
          <label htmlFor="esOOG">
            <FontAwesomeIcon
              icon={faUpRightAndDownLeftFromCenter}
              className={styles.icon}
            />
            {t('oog')}
          </label>
        </div>
        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esReeferOperativo"
            checked={formData.esReeferOperativo}
            onChange={() => handleCheckboxChange('esReeferOperativo')}
            disabled={formData.entregaDirecta}
          />
          <label htmlFor="esReeferOperativo">
            <FontAwesomeIcon icon={faSnowflake} className={styles.icon} />
            {t('operativeReefer')}
          </label>
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <Button
          type="submit"
          label=""
          severity="success"
          className={styles.calcButton}
          loading={isLoading}
        >
          <FontAwesomeIcon icon={faCalculator} className={styles.icon} />
          {t('calculate')}
        </Button>
      </div>
      <span className={styles.disclaimer}>
        {t('calculationDisclaimerImport')}
      </span>
    </form>
  );
};
